import _kebabCase from 'lodash/kebabCase';
import _get from 'lodash/get';
import { Google, Pixall } from '../analytics/index.js';
import { sanitizeValue } from './helpers/index.js';
const getEventDataByPagetype = (pageName, selectedItem) => {
  let eventResult;
  switch (pageName) {
    case 'MAKE':
      eventResult = {
        makeId: sanitizeValue(selectedItem.code),
        makeName: sanitizeValue(selectedItem.pageValue)
      };
      break;
    case 'STYLE':
      eventResult = {
        bodyStyle: sanitizeValue(selectedItem.pageValue)
      };
      break;
    default:
      eventResult = null;
  }
  return eventResult;
};
const psxBLPBaseAnalyticsObj = {
  eventSource: 'link',
  eventType: 'click',
  pageId: 'syc_psx_buy',
  pageType: 'own',
  program: 'DealerDotCom'
};

/**
 * @summary Sends Google event when recent search is clicked
 * @cmp rcnt_srch_v
 * @events event1
 */
export async function recentSearchClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;
  const {
    label = ''
  } = data;
  const textCopy = _kebabCase(label);
  if (label) {
    Google.sendClickEvent({
      eventElement: 'search-car',
      eventResult: `cta::srp::continue-your-search::${textCopy}`,
      eventNonInteractive: false
    }, option);
    Pixall.sendClickEvent({
      eventSource: 'link',
      label: `cta::srp::continue-your-search::${textCopy}`,
      hasListingType: false
    }, dataLayer, option);
  }
  return {};
}

/**
 * @summary Sends Google event for recent search impression
 */
export async function recentSearchImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const googleData = {
    eventElement: 'search-car',
    eventResult: 'cta::srp::continue-your-search',
    eventNonInteractive: true
  };
  Google.sendImpressions(metadata, googleData, option);
  return {};
}

/**
 * @summary Sends Analytics event when search near you CTA is clicked
 * @cmp srch_mdl_btn | srch_sty_btn | cnr_all_cta | srch_btn
 * @events event1
 */

export async function searchNearYouClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;

  // eslint-disable-next-line camelcase
  const {
    co_txt_url,
    pageType,
    selectedItem
  } = data;
  const withEventData = pageType === 'MAKE' || pageType === 'STYLE';
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'search',
    eventResult: `cta::srp::${sanitizeValue(co_txt_url)}`,
    ...(withEventData && {
      eventData: {
        vehicle: {
          catalog: dataLayer.catalog,
          ...getEventDataByPagetype(pageType, selectedItem)
        }
      }
    })
  }, option);
  return {};
}

/**
 * @summary Sends Google event when search by make grid is clicked
 * used for CTA and each make click in uc, nc, cpo and style page
 * @cmp make-grid
 * @events event1
 */
export const searchByMakeClick = function (metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;

  // eslint-disable-next-line camelcase
  const {
    co_txt_url = '',
    pageType = '',
    selectedItem = {}
  } = data;
  const linkType = selectedItem?.linkType || '';
  const regex = /^(cta|tile|text-link)$/;
  const isValidlinkTypePrefix = regex.test(selectedItem.linkType);
  const isCTAButton = linkType === 'cta' || '';
  const withEventData = pageType === 'STYLE';
  const getStylePageEventData = () => {
    if (isCTAButton) {
      return {
        bodyStyle: sanitizeValue(selectedItem?.pageValue)
      };
    }
    return {
      makeId: sanitizeValue(selectedItem?.code),
      makeName: sanitizeValue(selectedItem?.label)
    };
  };
  if (isValidlinkTypePrefix) {
    // eslint-disable-next-line camelcase
    const eventResultValue = isCTAButton ? co_txt_url : selectedItem?.label;
    Google.sendClickEvent({
      eventElement: 'search-car',
      eventResult: `${sanitizeValue(linkType)}::srp::${sanitizeValue(eventResultValue)}`,
      ...(withEventData && {
        eventData: {
          vehicle: {
            catalog: dataLayer.catalog,
            ...getStylePageEventData()
          }
        }
      })
    }, option);
  }
  return {};
};

/**
 * @summary Sends Google & Pixall event for recent search toggle in SRP
 */
export async function srpRecentSearchToggle(_temp, option) {
  let {
    data = {},
    dataLayer = {}
  } = _temp === void 0 ? {} : _temp;
  const {
    showRecentSearches
  } = data;
  Pixall.sendClickEvent({
    eventSource: 'personalization',
    label: `rcnt_srch_${showRecentSearches ? 'open' : 'close'}`
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'click',
    eventResult: `cta::${showRecentSearches ? 'show' : 'hide'}::my-recent-searches`
  }, option);
  return {};
}

/**
 * @summary Sends Analytics event when most recent vehicle is clicked
 * @cmp mdl_srch
 * @events event1
 */
export const mostSearchClick = function (metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;

  // eslint-disable-next-line camelcase
  const {
    pageType,
    isCTAButton = false,
    co_txt_url,
    selectedItem
  } = data;
  const getSelectedItemEventData = () => {
    if (pageType === 'MAKE') {
      return {
        modelId: sanitizeValue(selectedItem.modelId),
        modelName: sanitizeValue(selectedItem.modelName),
        makeName: sanitizeValue(selectedItem.makeName),
        makeId: sanitizeValue(selectedItem.makeId)
      };
    }
    if (pageType === 'STYLE') {
      return {
        modelId: sanitizeValue(selectedItem.modelId),
        modelName: sanitizeValue(selectedItem.modelName),
        makeName: sanitizeValue(selectedItem.makeName),
        makeId: sanitizeValue(selectedItem.makeId),
        bodyStyle: sanitizeValue(selectedItem.pageCode)
      };
    }
    return false;
  };
  const eventDataValue = isCTAButton ? getEventDataByPagetype(pageType, selectedItem) : getSelectedItemEventData();
  const eventPrefix = isCTAButton ? 'cta' : 'tile';
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'search',
    eventResult: `${eventPrefix}::srp::${sanitizeValue(co_txt_url)}`,
    ...(eventDataValue && {
      eventData: {
        vehicle: {
          catalog: dataLayer.catalog,
          ...eventDataValue
        }
      }
    })
  }, option);
  return {};
};

/**
 * @summary Sends Analytics event when CPO search is clicked
 * @cmp srch_cert_make
 * @events event1
 */
export const cpoSearchClick = async function (metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;

  // eslint-disable-next-line camelcase
  const {
    pageType,
    co_txt_url,
    selectedItem
  } = data;
  const eventDataValue = getEventDataByPagetype(pageType, selectedItem);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'search',
    eventResult: `cta::srp::${sanitizeValue(co_txt_url)}`,
    ...(eventDataValue && {
      eventData: {
        vehicle: {
          catalog: dataLayer.catalog,
          ...eventDataValue
        }
      }
    })
  }, option);
  return {};
};

/**
 * @summary Sends Google event when CPO learn more is clicked
 * @cmp ob_link
 * @events event1
 */
export const cpoLearnMoreClick = async (metadata, option) => {
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: 'cta::slp_cpo::learn-more-about-cpo'
  }, option);
  return {};
};

/**
 * @summary Sends Analytics event when find models by make is clicked
 * @cmp find_car_sale
 * @events event1
 */
export const findModelsByMakeClick = async function (metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;

  // eslint-disable-next-line camelcase
  const {
    co_txt_url,
    selectedItem
  } = data;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'search',
    eventResult: `text-link::srp::${sanitizeValue(co_txt_url)}`,
    ...(selectedItem && {
      eventData: {
        vehicle: {
          catalog: dataLayer.catalog,
          modelId: selectedItem.modelId?.toLowerCase(),
          modelName: selectedItem.modelName?.toLowerCase(),
          makeId: selectedItem.makeId?.toLowerCase(),
          makeName: selectedItem.makeName?.toLowerCase(),
          year: selectedItem.year?.toLowerCase()
        }
      }
    })
  }, option);
  return {};
};

/**
 * Where is this used?
 * @summary Sends Google event when deeplink link is clicked
 * @cmp passed-through
 * @events event1
 */
export async function deepLinkClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data = {},
    dataLayer
  } = metadata;
  const {
    cmp = ''
  } = data;
  const googleData = {
    eventData: {},
    eventNonInteractive: true
  };
  if (cmp === 'srch_cert_car') {
    googleData.eventData = {
      vehicle: {
        type: 'Cars'
      }
    };
  }
  let hasListingType = false;
  let label = '';
  switch (cmp) {
    case 'syc_btn':
      googleData.eventElement = 'sell-your-car';
      label = 'cta::center_syc::place-an-ad';
      hasListingType = true;
      break;
    case 'val_car':
      googleData.eventElement = 'own-path';
      label = 'cta::eval_syc::value-a-car';
      hasListingType = false;
      break;
    case 'str_my_off':
      googleData.eventElement = 'ico';
      label = 'tile::homepage::start-your-offer';
      hasListingType = true;
      break;
    case 'srch_cert_car':
      googleData.eventElement = 'search-car';
      label = 'cta::slp_cpo::find-certified-cars';
      hasListingType = false;
      break;
    default:
      googleData.eventElement = '';
      hasListingType = false;
      break;
  }
  googleData.eventResult = label;
  if (googleData.eventElement) {
    Google.sendClickEvent(googleData, option);
  }
  Pixall.sendClickEvent({
    eventSource: 'link',
    label,
    hasListingType
  }, dataLayer, option);
  return {};
}
export function privateSellerCarsInMyArea(metadata, option) {
  const {
    data = {},
    dataLayer = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::srp:private-seller-cars-in-my-area',
    hasListingType: 'false',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  const clickObject = {
    event: 'analytics_event',
    category: 'search-car',
    eventAction: 'click',
    eventResult: 'text-link::srp:private-seller-cars-in-my-area',
    eventNonInteractive: false,
    vehicle: {
      catalog: 'atc',
      condition: 'used'
    }
  };
  Google.sendClickEvent(clickObject, option);
  Pixall.sendClickEvent(pixallDataObject, dataLayer, option);
  return clickObject;
}
export function privateSellerTrucksInMyArea(metadata, option) {
  const {
    data = {},
    dataLayer = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::srp:private-seller-trucks-in-my-area',
    hasListingType: 'false',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  const clickObject = {
    event: 'analytics_event',
    category: 'search-car',
    eventAction: 'click',
    eventResult: 'text-link::srp:private-seller-trucks-in-my-area',
    eventNonInteractive: false,
    vehicle: {
      bodyStyle: 'truck',
      catalog: 'atc',
      condition: 'used'
    }
  };
  Google.sendClickEvent(clickObject, option);
  Pixall.sendClickEvent(pixallDataObject, dataLayer, option);
  return clickObject;
}

/**
 * @summary Sends Google & Pixall event for recent search click in SRP
 */
export function myRecentSearchClick(_ref, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref;
  const textContent = sanitizeValue(_get(data, 'textContent', ''));
  const searchZip = _get(state, 'query.zip', '');
  const googleData = {
    category: 'search-car',
    event: 'analytics_event',
    eventAction: 'filter',
    eventNonInteractive: false,
    eventResult: `most-recent-search::${textContent}`
  };
  const pixallEventData = {
    eventSource: 'application',
    label: `most-recent-search::${textContent}`,
    zipCode: searchZip,
    filterValues: data?.filterValues || {}
  };
  Google.sendClickEvent(googleData, option);
  Pixall.sendClickEvent(pixallEventData, dataLayer, option);
  return {};
}

/**
 * @summary Sends Google impression event for most recent search in SRP
 */
export function myRecentSearchImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const googleData = {
    eventElement: 'search-car',
    eventResult: 'most-recent-search',
    eventNonInteractive: true
  };
  Google.sendImpressions(metadata, googleData, option);
  return {};
}
export default {
  cpoLearnMoreClick,
  cpoSearchClick,
  deepLinkClick,
  findModelsByMakeClick,
  mostSearchClick,
  myRecentSearchClick,
  myRecentSearchImpression,
  recentSearchClick,
  searchNearYouClick,
  searchByMakeClick,
  srpRecentSearchToggle,
  privateSellerCarsInMyArea,
  privateSellerTrucksInMyArea
};